import React, { useState } from 'react';
import { Row, Col } from 'react-bootstrap';
import dayjs from 'dayjs';
import './l120HomeHmi.css';

// ───────── helpers ─────────
const fmt = (v, d = 1) =>
  v == null || v === '' || isNaN(Number(v)) ? '—' : Number(v).toFixed(d);

const StatusPill = ({ on, onLabel = 'ON', offLabel = 'OFF', children }) => (
  <div
    style={{
      background: on ? '#85ff85' : '#ff8a8a',
      color: on ? '#0a3d0a' : '#5a0000',
      padding: '6px 14px',
      borderRadius: 8,
      fontWeight: 700,
      textAlign: 'center',
      minWidth: 60,
      display: 'inline-block',
    }}
  >
    {children ?? (on ? onLabel : offLabel)}
  </div>
);

const AlarmPill = ({ on, onLabel, offLabel }) => (
  <div
    style={{
      background: on ? '#ff5a5a' : '#85ff85',
      color: on ? '#fff' : '#0a3d0a',
      padding: '10px 14px',
      borderRadius: 8,
      fontWeight: 700,
      textAlign: 'center',
    }}
  >
    {on ? onLabel : offLabel}
  </div>
);

const TempStatus = ({ alarm, warning }) => {
  if (alarm)
    return (
      <div style={pillBase('#ff5a5a', '#fff')}>TEMPERATURĂ NU OK</div>
    );
  if (warning)
    return (
      <div style={pillBase('#ffd54d', '#5a3a00')}>TEMPERATURĂ WARNING</div>
    );
  return (
    <div style={pillBase('#85ff85', '#0a3d0a')}>TEMPERATURĂ OK</div>
  );
};

const pillBase = (bg, color) => ({
  background: bg,
  color,
  padding: 10,
  borderRadius: 8,
  fontWeight: 700,
  textAlign: 'center',
});

const BtnCard = ({ label, on }) => {
  // on === null => TBD (var not wired yet)
  const isUnknown = on === null;
  return (
    <div
      className="card-modern"
      style={{
        textAlign: 'center',
        background: isUnknown ? '#eee' : on ? '#85ff85' : '#ff8a8a',
      }}
    >
      <div className="card-title" style={{ marginBottom: 8 }}>
        {label}
      </div>
      <div style={{ fontWeight: 800 }}>
        {isUnknown ? 'TBD' : on ? 'PORNIT' : 'OPRIT'}
      </div>
    </div>
  );
};

const MotorCard = ({ title, on, sub }) => (
  <div className="card-modern" style={{ textAlign: 'center' }}>
    <div className="card-title">{title}</div>
    <StatusPill on={on} />
    {sub && (
      <div className="value-label" style={{ marginTop: 8 }}>
        {sub}
      </div>
    )}
  </div>
);

// ───────── HOME PAGE ─────────
const L120HomePage = ({ live, feed }) => (
  <div className="l120-modern-container">
    {/* TOP BAR */}
    <div className="topbar-modern">
      <div className="topbar-col">
        {/* Was dayjs().format('HH:mm:ss') — the live wall clock. On a frozen
            feed that is a second hand ticking beside numbers that have not
            moved for hours: the most reassuring thing on the screen, and
            completely false. It now answers the question the field was
            standing in for — how old is what I am looking at. */}
        <div className="top-label">{feed?.stale ? 'DATE VECHI' : 'Date PLC'}</div>
        <div
          className="top-time"
          title={feed?.title || undefined}
          style={feed?.stale ? { color: '#F87171' } : undefined}
        >
          {feed?.label ? `${feed.label} în urmă` : dayjs().format('HH:mm:ss')}
        </div>
      </div>
      <div className="topbar-col">
        <div className="top-label">Viteză linie actuală</div>
        <div className="top-value-row">
          <span className="top-value">{fmt(live.LINE_SPEED_ACT, 1)}</span>
          <span className="top-unit">m/min</span>
        </div>
      </div>
      <div className="topbar-col">
        <div className="top-label">Metru actual</div>
        <div className="top-value-row">
          <span className="top-value">{fmt(live.METER_ACT, 1)}</span>
          <span className="top-unit">m</span>
        </div>
      </div>
      <div className="topbar-col">
        <div className="top-label">Line start</div>
        <div style={{ marginTop: 6 }}>
          <StatusPill on={!!live.LINE_START_RELAY} />
        </div>
      </div>
    </div>

    {/* DIAMETRE */}
    <Row className="mt-3 g-3">
      <Col md={6}>
        <DiaCard
          title="Diametru FILLING"
          on={!!live.FILLING_DIA_ON_OFF}
          set={live.FILLING_SET_DIA}
          act={live.FILLING_DIA_ACT_VALUE}
        />
      </Col>
      <Col md={6}>
        <DiaCard
          title="Diametru JACKETING"
          on={!!live.JACKETING_DIA_ON_OFF}
          set={live.JACKETING_SET_DIA}
          act={live.JACKETING_DIA_ACT_VALUE}
        />
      </Col>
    </Row>

    {/* MOTOARE */}
    <Row className="mt-3 g-3">
      <Col md={3}>
        <MotorCard title="Suflantă" on={!!live.BLOWER_MOTOR_ON_OFF} />
      </Col>
      <Col md={3}>
        <MotorCard
          title="Apă caldă motor"
          on={!!live.HOT_WATER_MOTOR_ON_OFF}
        />
      </Col>
      <Col md={3}>
        <MotorCard
          title="Încălzire (heat res)"
          on={!!live.HEAT_RES_ON_OFF}
          sub={`Bazin: ${fmt(live.HEAT_POOL_ACT_VALUE, 0)} °C`}
        />
      </Col>
      <Col md={3}>
        <MotorCard title="Apă rece motor" on={!!live.COLD_WATER_MOTOR_ON_OFF} />
      </Col>
    </Row>

    {/* ALARME PAY OFF / TAKE UP */}
    <Row className="mt-3 g-3">
      <Col md={6}>
        <div className="card-modern">
          <div className="card-title">Pay-off STP 1</div>
          <AlarmPill
            on={!!live.PAY_OFF_STP1_ALARM}
            onLabel="ALARMĂ PORNITĂ"
            offLabel="ALARMĂ OPRITĂ"
          />
          <Row className="mt-3 g-2">
            <Col>
              <StatusPill on={!!live.PAY_OFF_STP1_PINTLE_OPEN_BUTTON}>
                PINTLE OPEN {live.PAY_OFF_STP1_PINTLE_OPEN_BUTTON ? 'ON' : 'OFF'}
              </StatusPill>
            </Col>
            <Col>
              <StatusPill on={!!live.PAY_OFF_STP1_PINTLE_CLOSE_BUTTON}>
                PINTLE CLOSE{' '}
                {live.PAY_OFF_STP1_PINTLE_CLOSE_BUTTON ? 'ON' : 'OFF'}
              </StatusPill>
            </Col>
          </Row>
        </div>
      </Col>
      <Col md={6}>
        <div className="card-modern">
          <div className="card-title">Take-up</div>
          <AlarmPill
            on={!!live.TAKEUP_ALARM_ON_OFF}
            onLabel="ALARMĂ PORNITĂ"
            offLabel="ALARMĂ OPRITĂ"
          />
        </div>
      </Col>
    </Row>

    {/* EXTRUDERE */}
    <Row className="mt-3 g-3">
      <Col md={6}>
        <ExtruderSummary
          name="E 120 F (Filler)"
          on={!!live.E120_F_ON_OFF}
          rpm={live.E120_F_SCREW_ACT_RPM}
          current={live.E120_F_CURRENT_ACT}
          percent={live.E120_F_ACT_PERCENTEC_VALUE}
        />
      </Col>
      <Col md={6}>
        <ExtruderSummary
          name="E 120 J (Jacketing)"
          on={!!live.E120_J_ON_OFF}
          rpm={live.E120_J_SCREW_ACT_RPM}
          current={live.E120_J_CURRENT_ACT}
          percent={live.E120_J_ACT_PERCENTEC_VALUE}
        />
      </Col>
    </Row>

    {/* SENILE */}
    <Row className="mt-3 g-3">
      <Col md={6}>
        <CatCard
          title="Intrare senilă"
          on={!!live.ENTRY_CAT_ON_OFF}
          percent={live.ENTRY_CAT_ACT_PERCENTEC_VALUE}
          current={live.ENTRY_CAT_CURRENT}
        />
      </Col>
      <Col md={6}>
        <CatCard
          title="Ieșire senilă"
          on={!!live.EXIT_CAT_ON_OFF}
          percent={live.EXIT_CAT_ACT_PERCENTEC_VALUE}
          current={live.EXIT_CAT_CURRENT}
        />
      </Col>
    </Row>
  </div>
);

const DiaCard = ({ title, on, set, act }) => (
  <div className="card-modern">
    <div className="card-title">{title}</div>
    <Row className="align-items-center g-3">
      <Col xs="auto">
        <StatusPill on={on} onLabel="LASER ON" offLabel="LASER OFF" />
      </Col>
      <Col>
        <div className="value-label">SET</div>
        <div className="value-number">
          {fmt(set, 2)}
          <span className="unit"> mm</span>
        </div>
      </Col>
      <Col>
        <div className="value-label">ACT</div>
        <div className="value-number">
          {fmt(act, 2)}
          <span className="unit"> mm</span>
        </div>
      </Col>
    </Row>
  </div>
);

const ExtruderSummary = ({ name, on, rpm, current, percent }) => (
  <div className="card-modern">
    <div className="card-title">{name}</div>
    <Row className="align-items-center g-3">
      <Col xs="auto">
        <StatusPill on={on} />
      </Col>
      <Col>
        <div className="value-label">Șnec RPM</div>
        <div className="value-number">{fmt(rpm, 1)}</div>
      </Col>
      <Col>
        <div className="value-label">Putere %</div>
        <div className="value-number">{fmt(percent, 1)}</div>
      </Col>
      <Col>
        <div className="value-label">Curent A</div>
        <div className="value-number">{fmt(current, 1)}</div>
      </Col>
    </Row>
  </div>
);

const CatCard = ({ title, on, percent, current }) => (
  <div className="card-modern">
    <div className="card-title">{title}</div>
    <Row className="align-items-center g-3">
      <Col xs="auto">
        <StatusPill on={on} />
      </Col>
      <Col>
        <div className="value-label">Viteză %</div>
        <div className="value-number">{fmt(percent, 1)}</div>
      </Col>
      <Col>
        <div className="value-label">Curent A</div>
        <div className="value-number">{fmt(current, 1)}</div>
      </Col>
    </Row>
  </div>
);

// ───────── EXTRUDER PAGE (F sau J) ─────────
const L120ExtruderPage = ({ live, prefix, label }) => {
  const p = (k) => live[`${prefix}_${k}`];

  const tempAlarm = !!p('TEMP_NOT_OK');
  const tempWarning = !!p('TEMP_WARNING_NOT_OK');

  const setZones = [
    p('SET_TEMP_OP_Z1'),
    p('SET_TEMP_OP_Z2'),
    p('SET_TEMP_OP_Z3'),
    p('SET_TEMP_OP_Z4'),
    p('SET_TEMP_OP_Z5'),
    p('SET_TEMP_OP_CLAMP'),
    p('SET_TEMP_OP_ADAP'),
    p('SET_TEMP_OP_HEAD1'),
    p('SET_TEMP_OP_HEAD2'),
  ];
  const actZones = [
    p('ACT_TEMP_Z1'),
    p('ACT_TEMP_Z2'),
    p('ACT_TEMP_Z3'),
    p('ACT_TEMP_Z4'),
    p('ACT_TEMP_Z5'),
    p('ACT_TEMP_CLAMP'),
    p('ACT_TEMP_ADAPTOR'),
    p('ACT_TEMP_HEAD1'),
    p('ACT_TEMP_HEAD2'),
  ];
  const zoneLabels = ['1', '2', '3', '4', '5', 'CL', 'AD', 'H1', 'H2'];

  // J head 6-9 nu e inca in vars (user-marked TBD).
  const head69OnOff =
    prefix === 'E120_F'
      ? !!p('HEAD_6_9_HEAT_ON_OFF')
      : prefix === 'E120_J'
      ? null
      : !!p('HEAD_6_9_HEAT_ON_OFF');

  return (
    <div className="extruder-container">
      {/* TOP ROW: identitate + temp deviation + stand-by temp */}
      <Row className="g-3 mb-3">
        <Col md={4}>
          <div className="card-modern" style={{ textAlign: 'center' }}>
            <div className="card-title">{label}</div>
            <StatusPill on={!!p('ON_OFF')} />
          </div>
        </Col>
        <Col md={4}>
          <div className="card-modern" style={{ textAlign: 'center' }}>
            <div className="card-title">Abatere temperatură</div>
            <div className="value-number">
              {fmt(p('DEVIATION_LIMIT'), 0)} <span className="unit">°C</span>
            </div>
          </div>
        </Col>
        <Col md={4}>
          <div className="card-modern" style={{ textAlign: 'center' }}>
            <div className="card-title">Temperatură în stand-by</div>
            <div className="value-number">
              {fmt(p('HEATING_SET_TEMP_OP_STND_BY'), 0)}{' '}
              <span className="unit">°C</span>
            </div>
          </div>
        </Col>
      </Row>

      <Row className="g-3 mb-3">
        <Col md={12}>
          <TempStatus alarm={tempAlarm} warning={tempWarning} />
        </Col>
      </Row>

      {/* BUTOANE INCALZIRE */}
      <Row className="g-3 mb-3">
        <Col md={6}>
          <BtnCard
            label="Cilindru 1-5 încălzire + ventilare"
            on={!!p('BAREL_1_5_HEAT_FAN_ON_OFF')}
          />
        </Col>
        <Col md={6}>
          <BtnCard label="Cap 6-9 încălzire" on={head69OnOff} />
        </Col>
      </Row>

      {/* PERFORMANTA */}
      <Row className="g-3 mb-3">
        <Col md={6}>
          <div className="card-modern">
            <div className="card-title">Viteză + curent</div>
            <Row>
              <Col>
                <div className="value-label">Șnec</div>
                <div className="value-number">
                  {fmt(p('SCREW_ACT_RPM'), 1)}{' '}
                  <span className="unit">RPM</span>
                </div>
              </Col>
              <Col>
                <div className="value-label">Curent</div>
                <div className="value-number">
                  {fmt(p('CURRENT_ACT'), 1)} <span className="unit">A</span>
                </div>
              </Col>
            </Row>
          </div>
        </Col>
        <Col md={6}>
          <div className="card-modern">
            <div className="card-title">Stare</div>
            <Row className="align-items-center">
              <Col xs="auto">
                <StatusPill on={!!p('ON_OFF')} />
              </Col>
              <Col>
                <div className="value-label">Putere %</div>
                <div className="value-number">
                  {fmt(p('ACT_PERCENTEC_VALUE'), 1)}{' '}
                  <span className="unit">%</span>
                </div>
              </Col>
            </Row>
          </div>
        </Col>
      </Row>

      {/* TABEL ZONE WZ + 1-9 */}
      <Row className="g-3">
        <Col md={12}>
          <div className="card-modern">
            <div className="card-title">Zone temperatură (SET / ACT °C)</div>
            <div className="zones-table">
              <div className="zones-row title-row">
                <div className="cell title">—</div>
                <div className="cell title">WZ</div>
                {zoneLabels.map((l) => (
                  <div key={l} className="cell title">
                    {l}
                  </div>
                ))}
              </div>
              <div className="zones-row">
                <div className="cell title">SET</div>
                <div className="cell set">—</div>
                {setZones.map((z, i) => (
                  <div key={i} className="cell set">
                    {fmt(z, 0)}
                  </div>
                ))}
              </div>
              <div className="zones-row">
                <div className="cell title">ACT</div>
                <div className="cell act">{fmt(p('ACT_TEMP_WZ'), 0)}</div>
                {actZones.map((z, i) => (
                  <div key={i} className="cell act">
                    {fmt(z, 0)}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </Col>
      </Row>
    </div>
  );
};

// ───────── METER PAGE ─────────
const L120MeterPage = ({ live, feed }) => (
  <div className="metraj-container">
    {/* HEADER */}
    <Row className="g-3 mb-3">
      <Col md={3}>
        <div
          className="card-modern"
          style={{
            background: '#0bb5aa',
            color: 'white',
            textAlign: 'center',
          }}
        >
          <div style={{ fontWeight: 800, fontSize: '1.2rem' }}>ENG</div>
          <div
            className="value-label"
            style={{ color: 'rgba(255,255,255,0.85)' }}
          >
            {feed?.label ? `${feed.label} în urmă` : dayjs().format('HH:mm:ss')}
          </div>
        </div>
      </Col>
      <Col md={3}>
        <div className="card-modern" style={{ textAlign: 'center' }}>
          <div className="card-title">ACT LINE SPEED</div>
          <div className="value-number" style={{ color: '#d10000' }}>
            {fmt(live.LINE_SPEED_ACT, 1)}{' '}
            <span className="unit">m/min</span>
          </div>
        </div>
      </Col>
      <Col md={3}>
        <div className="card-modern" style={{ textAlign: 'center' }}>
          <div className="card-title">ACTUAL METER</div>
          <div className="value-number">{fmt(live.METER_ACT, 1)}</div>
        </div>
      </Col>
      <Col md={3}>
        <div
          className="card-modern"
          style={{
            background: '#ff6cc4',
            color: 'white',
            textAlign: 'center',
          }}
        >
          <div style={{ fontWeight: 800, fontSize: '1.2rem' }}>KABMAK</div>
        </div>
      </Col>
    </Row>

    {/* DIA CONTROL CARDS */}
    <Row className="g-3">
      <Col md={6}>
        <DiaControlCard
          title="FILLING DIAMETER CONTROL"
          on={!!live.FILLING_DIA_ON_OFF}
          set={live.FILLING_SET_DIA}
          act={live.FILLING_DIA_ACT_VALUE}
        />
      </Col>
      <Col md={6}>
        <DiaControlCard
          title="JACKETING DIAMETER CONTROL"
          on={!!live.JACKETING_DIA_ON_OFF}
          set={live.JACKETING_SET_DIA}
          act={live.JACKETING_DIA_ACT_VALUE}
        />
      </Col>
    </Row>

    {/* METRAJ EXTRA (set / act / total / resets) */}
    <Row className="g-3 mt-1">
      <Col md={6}>
        <div className="card-modern">
          <div className="card-title">Metraj</div>
          <Row className="g-2">
            <Col>
              <div className="metraj-box yellow">
                <div className="label">SET</div>
                <div className="big-value" style={{ fontSize: '1.4rem' }}>
                  {fmt(live.SET_METER, 1)} m
                </div>
              </div>
            </Col>
            <Col>
              <div className="metraj-box cyan">
                <div className="label">ACT</div>
                <div className="big-value" style={{ fontSize: '1.4rem' }}>
                  {fmt(live.METER_ACT, 1)} m
                </div>
              </div>
            </Col>
            <Col>
              <div className="metraj-box cyan">
                <div className="label">TOTAL</div>
                <div className="big-value" style={{ fontSize: '1.4rem' }}>
                  {fmt(live.METER_TOTAL, 1)} m
                </div>
              </div>
            </Col>
          </Row>
        </div>
      </Col>
      <Col md={6}>
        <div className="card-modern">
          <div className="card-title">Resetări metru</div>
          <Row className="g-2">
            <Col>
              <StatusPill
                on={!!live.METER_RESET}
                onLabel="METER RESET — ON"
                offLabel="METER RESET — OFF"
              />
            </Col>
            <Col>
              <StatusPill
                on={!!live.TOTAL_METER_RESET}
                onLabel="TOTAL RESET — ON"
                offLabel="TOTAL RESET — OFF"
              />
            </Col>
          </Row>
        </div>
      </Col>
    </Row>
  </div>
);

const DiaControlCard = ({ title, on, set, act }) => (
  <div className="card-modern">
    <div
      className="card-title"
      style={{ textAlign: 'center', fontSize: '1.1rem' }}
    >
      <u>{title}</u>
    </div>
    <Row className="align-items-center g-3 mt-1">
      <Col md={4}>
        <div
          style={{
            background: on ? '#85ff85' : '#ff8a8a',
            padding: '14px',
            borderRadius: 10,
            textAlign: 'center',
            fontWeight: 800,
            minHeight: 90,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {on ? 'LASER ON' : 'LASER OFF'}
        </div>
      </Col>
      <Col md={8}>
        <div className="metraj-box yellow mb-2">
          <Row>
            <Col>
              <div className="label">SET</div>
            </Col>
            <Col>
              <div className="big-value" style={{ fontSize: '1.4rem' }}>
                {fmt(set, 2)} mm
              </div>
            </Col>
          </Row>
        </div>
        <div className="metraj-box cyan">
          <Row>
            <Col>
              <div className="label">ACT</div>
            </Col>
            <Col>
              <div className="big-value" style={{ fontSize: '1.4rem' }}>
                {fmt(act, 2)} mm
              </div>
            </Col>
          </Row>
        </div>
      </Col>
    </Row>
    <Row className="g-2 mt-2">
      <Col md={6}>
        <div className="metraj-box yellow">
          <Row>
            <Col>
              <div className="label">TOLERANȚĂ</div>
            </Col>
            <Col>
              <div className="big-value" style={{ fontSize: '1.1rem' }}>
                —
              </div>
            </Col>
          </Row>
        </div>
      </Col>
      <Col md={6}>
        <div className="metraj-box yellow">
          <Row>
            <Col>
              <div className="label">COEFF</div>
            </Col>
            <Col>
              <div className="big-value" style={{ fontSize: '1.1rem' }}>
                —
              </div>
            </Col>
          </Row>
        </div>
      </Col>
    </Row>
  </div>
);

// ───────── MAIN COMPONENT ─────────
const L120Hmi = ({ machineLive = {}, feed = null }) => {
  const [page, setPage] = useState('HOME');

  const renderPage = () => {
    switch (page) {
      case 'HOME':
        return <L120HomePage live={machineLive} feed={feed} />;
      case 'EXTRUDER_F':
        return (
          <L120ExtruderPage
            live={machineLive}
            prefix="E120_F"
            label="E-120/F (Filler)"
          />
        );
      case 'EXTRUDER_J':
        return (
          <L120ExtruderPage
            live={machineLive}
            prefix="E120_J"
            label="E-120/J (Jacketing)"
          />
        );
      case 'METER':
        return <L120MeterPage live={machineLive} feed={feed} />;
      default:
        return <L120HomePage live={machineLive} />;
    }
  };

  const tab = (key, label) => (
    <div
      className={`menu-btn ${page === key ? 'active' : ''}`}
      onClick={() => setPage(key)}
    >
      {label}
    </div>
  );

  return (
    <div className="l120-hmi-wrapper">
      {renderPage()}
      <div className="bottom-menu-modern">
        {tab('HOME', 'Home')}
        {tab('EXTRUDER_F', 'Extruder F')}
        {tab('EXTRUDER_J', 'Extruder J')}
        {tab('METER', 'Meter Page')}
      </div>
    </div>
  );
};

export default L120Hmi;
